// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import ShipToSelector, { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import CreateNewAddressModal, {
    CreateNewAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, {
    EditExistingAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";
import ShippingAddressForm, {
    ShippingAddressFormStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { Validator } from "@tps/Overrides/Pages/CheckoutShippingPage";
import { Carriers } from "@tps/XDL/constants";
import { doScroll, modalButtonsActionStyles } from "@tps/XDL/Utilities";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

/* 
    XNG-Change: some changes on structure and functions to match design and FRD
*/

interface OwnProps {
    address: ShipToModel;
    currentBillToId: string | undefined;
    newAddress?: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    onChange: (address: ShipToModel, isEdit: boolean) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ShippingAddressStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
        shipTosDataView: cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined,
        cart,
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    setAddressBookOverlayIsOpen,
    // XNG-Change
    updateCart: makeHandlerChainAwaitable(updateCart),
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface ShippingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    useBillingAddressButton?: ButtonPresentationProps;
    // XNG-Changes
    oneTimeAddressFormTitle?: TypographyProps;
    oneTimeAddressForm?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    oneTimeAddressModal?: ModalPresentationProps;
    editAddressLink?: LinkPresentationProps;
    addressOptionRadio?: RadioProps;
    addressLinkWrapper?: InjectableCss;
    oneTimeShippingAddress?: ShippingAddressFormStyles;
    shippingAddressWarningText?: TypographyPresentationProps;
    multipleShippingAddressMessage?: TypographyPresentationProps;
}

export const shippingAddressStyles: ShippingAddressStyles = {
    container: { gap: 20 },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    oneTimeAddressGridItem: { width: 12 },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    useBillingAddressButton: {
        variant: "secondary",
        css: css`
            margin-top: 20px;
        `,
    },
};

const StyledForm = getStyledWrapper("form");

const ShippingAddress: FC<Props> = ({
    address,
    newAddress,
    oneTimeAddress,
    onChange,
    currentUserIsGuest,
    useBillingAddress,
    useOneTimeAddress,
    allowCreateNewShipToAddress,
    setUseBillingAddress,
    setUseOneTimeAddress,
    extendedStyles,
    currentBillToId,
    isUseBillingAddressDisabled,
    shippingAddressFormState,
    showUseBillingAddress,
    isShippingAddressUpdateRequired,
    countries,
    addressFieldDisplayCollection,
    theme,
    setAddressBookOverlayIsOpen,
    // XNG-Change
    updateCart,
    shipTosDataView,
    cart,
}) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isCreateNewAddressModalOpen, setIsCreateNewAddressModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shippingAddressStyles, extendedStyles));
    const [isOneTimeAddressModalOpen, setIsOneTimeAddressModalOpen] = useState(false);
    const checkoutShippingFormContext = useContext(CheckoutShippingFormContext);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isOneTieAddressSaved, setIsOneTieAddressSaved] = useState(false);
    const [prevAddress, setPrevAddress] = useState(address);
    const dispatch = useDispatch();

    // <XNG-Changes>
    const showMultipleShipping = useMemo(() => {
        const warehouses = JSON.parse(cart?.properties?.warehouses || "{}");
        let previousShippingType = "";
        for (const warehouseId in warehouses) {
            if (!warehouses[warehouseId].IsVerified) {
                continue;
            }
            const currentShippingType =
                warehouses[warehouseId].ShipVia?.toLowerCase() === Carriers.willCall ? "willCall" : "ship";
            if (!previousShippingType) {
                previousShippingType = currentShippingType;
            } else if (previousShippingType !== currentShippingType) {
                return true;
            }
        }
        return false;
    }, [cart?.properties?.warehouses]);
    // </XNG-Changes>

    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    // const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
    //     setUseOneTimeAddress({
    //         useOneTimeAddress: value,
    //     });

    // const useBillingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
    //     setUseBillingAddress({
    //         useBillingAddress: value,
    //     });

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        setPrevAddress(address);

        if (event.target.value === "onetime") {
            setIsOneTimeAddressModalOpen(true);
            setIsOneTieAddressSaved(false);
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
        } else if (event.target.value === "billing") {
            setIsOneTieAddressSaved(false);
            setUseBillingAddress({
                useBillingAddress: true,
            });
        } else if (event.target.value === "shipping") {
            const shipTosForBillTo = shipTosDataView?.value;
            let shipTo;
            if (shipTosForBillTo && shipTosForBillTo.length) {
                shipTo = shipTosForBillTo.find(o => o.isDefault);
                if (!shipTo) {
                    shipTo = shipTosForBillTo.find(o => !o.isNew && !o.oneTimeAddress && o.id !== cart?.billToId);
                }

                shipTo && setIsOneTieAddressSaved(false);
                shipTo && onChange(shipTo, false);
                if (oneTimeAddress) {
                    dispatch({
                        type: "Pages/CheckoutShipping/SetIsShippingAddressUpdateRequired",
                        isShippingAddressUpdateRequired: false,
                    });
                    setUseOneTimeAddress({
                        useOneTimeAddress: false,
                    });
                }
            }
        }
    };
    const handleCloseOneTimeModalAddress = () => {
        setIsOneTimeAddressModalOpen(false);
        // This means that the one time address is saved and the modal was opened for editing
        if (address.isNew && shippingAddressFormState && shippingAddressFormState.address.oneTimeAddress) {
            return;
        }
        if (!isOneTieAddressSaved) {
            if (prevAddress.id === cart?.billToId) {
                setUseBillingAddress({
                    useBillingAddress: true,
                });
                return;
            }
            // This case when the prevAddress was shipping address
            dispatch({
                type: "Pages/CheckoutShipping/SetIsShippingAddressUpdateRequired",
                isShippingAddressUpdateRequired: false,
            });
            dispatch({
                type: "Pages/CheckoutShipping/SetShippingAddress",
                address: prevAddress,
            });
            setUseOneTimeAddress({
                useOneTimeAddress: false,
            });
        }
    };

    const submitOneTimeAddress = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const validators: Validator[] = [];
        Object.keys(checkoutShippingFormContext.validators).forEach(key => {
            const validator = checkoutShippingFormContext.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        setIsFormSubmitted(true);

        if (results.every(o => o)) {
            setIsOneTimeAddressModalOpen(false);
            setIsOneTieAddressSaved(true);
            updateCart({});
            return;
        }
        doScroll();
    };

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        customerSelectorModalCloseHandler();
        onChange(shipTo, false);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsCreateNewAddressModalOpen(true);
    };

    const createNewAddressModalCloseHandler = () => setIsCreateNewAddressModalOpen(false);

    const newShippingAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        createNewAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address has been created."),
        });
    };

    const editCustomerHandler = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        customer: BaseAddressModel,
    ) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };

    return (
        <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText} as="p">
                    {translate("Shipping Address")}
                </Typography>
                {/* <XNG-Changes> */}
                <Typography {...styles.shippingAddressWarningText}>
                    {siteMessage("Custom_Shipping_Address_Warning")}
                </Typography>
                {showMultipleShipping && (
                    <Typography {...styles.multipleShippingAddressMessage}>
                        {siteMessage("Custom_Shipping_Address_Multiple")}
                    </Typography>
                )}
                {/* </XNG-Changes> */}
                {newAddress && (
                    <CreateNewAddressModal
                        newAddress={newAddress}
                        isModalOpen={isCreateNewAddressModalOpen}
                        onModalClose={createNewAddressModalCloseHandler}
                        onFormSubmit={newShippingAddressFormSubmitHandler}
                    />
                )}
                {addressBeingEdited && (
                    <EditExistingAddressModal
                        newAddress={addressBeingEdited}
                        isModalOpen={isEditAddressModalOpen}
                        onModalClose={editAddressModalCloseHandler}
                        onFormSubmit={editAddressFormSubmitHandler}
                    />
                )}
            </GridItem>
            <GridItem {...styles.oneTimeAddressGridItem}>
                <RadioGroup
                    value={
                        shippingAddressFormState
                            ? shippingAddressFormState.address.oneTimeAddress
                                ? "onetime"
                                : shippingAddressFormState.address.id === currentBillToId
                                ? "billing"
                                : "shipping"
                            : ""
                    }
                    onChangeHandler={addressTypeChangeHandler}
                >
                    {showUseBillingAddress && (
                        <Radio {...styles.addressOptionRadio} value="billing">
                            {translate("Use Billing Address")}
                            {shippingAddressFormState && shippingAddressFormState.address.id === currentBillToId && (
                                <StyledWrapper {...styles.addressLinkWrapper}>
                                    {!useOneTimeAddress && !currentUserIsGuest && !isShippingAddressUpdateRequired && (
                                        <AddressInfoDisplay
                                            {...address}
                                            // Xng-Changes
                                            address1={
                                                addressFieldDisplayCollection.address1?.isVisible
                                                    ? address.address1
                                                    : ""
                                            }
                                            address2={
                                                addressFieldDisplayCollection.address2?.isVisible
                                                    ? address.address2
                                                    : ""
                                            }
                                            address3={
                                                addressFieldDisplayCollection.address3?.isVisible
                                                    ? address.address3
                                                    : ""
                                            }
                                            address4={
                                                addressFieldDisplayCollection.address4?.isVisible
                                                    ? address.address4
                                                    : ""
                                            }
                                            attention={
                                                addressFieldDisplayCollection.attention?.isVisible
                                                    ? address.attention
                                                    : ""
                                            }
                                            city={addressFieldDisplayCollection.city?.isVisible ? address.city : ""}
                                            companyName={
                                                addressFieldDisplayCollection.companyName?.isVisible
                                                    ? address.companyName
                                                    : ""
                                            }
                                            firstName={
                                                addressFieldDisplayCollection.firstName?.isVisible
                                                    ? address.firstName
                                                    : ""
                                            }
                                            lastName={
                                                addressFieldDisplayCollection.lastName?.isVisible
                                                    ? address.lastName
                                                    : ""
                                            }
                                            postalCode={
                                                addressFieldDisplayCollection.postalCode?.isVisible
                                                    ? address.postalCode
                                                    : ""
                                            }
                                            phone={addressFieldDisplayCollection.phone?.isVisible ? address.phone : ""}
                                            fax={addressFieldDisplayCollection.fax?.isVisible ? address.fax : ""}
                                            email={addressFieldDisplayCollection.email?.isVisible ? address.email : ""}
                                            state={
                                                address.state && addressFieldDisplayCollection.state?.isVisible
                                                    ? address.state.abbreviation
                                                    : undefined
                                            }
                                            country={
                                                address.country && addressFieldDisplayCollection.country?.isVisible
                                                    ? address.country.abbreviation
                                                    : undefined
                                            }
                                            extendedStyles={styles.addressDisplay}
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                        </Radio>
                    )}
                    {(!currentUserIsGuest ||
                        (currentUserIsGuest && (allowCreateNewShipToAddress || !!newAddress || !!address))) && (
                        <>
                            <Radio {...styles.addressOptionRadio} value="shipping">
                                {translate("Use Shipping Address")}
                                {shippingAddressFormState &&
                                    !shippingAddressFormState.address.oneTimeAddress &&
                                    shippingAddressFormState.address.id !== currentBillToId && (
                                        <StyledWrapper {...styles.addressLinkWrapper}>
                                            {!useOneTimeAddress && !currentUserIsGuest && (
                                                <AddressInfoDisplay
                                                    {...address}
                                                    // Xng-Changes
                                                    address1={
                                                        addressFieldDisplayCollection.address1?.isVisible
                                                            ? address.address1
                                                            : ""
                                                    }
                                                    address2={
                                                        addressFieldDisplayCollection.address2?.isVisible
                                                            ? address.address2
                                                            : ""
                                                    }
                                                    address3={
                                                        addressFieldDisplayCollection.address3?.isVisible
                                                            ? address.address3
                                                            : ""
                                                    }
                                                    address4={
                                                        addressFieldDisplayCollection.address4?.isVisible
                                                            ? address.address4
                                                            : ""
                                                    }
                                                    attention={
                                                        addressFieldDisplayCollection.attention?.isVisible
                                                            ? address.attention
                                                            : ""
                                                    }
                                                    city={
                                                        addressFieldDisplayCollection.city?.isVisible
                                                            ? address.city
                                                            : ""
                                                    }
                                                    companyName={
                                                        addressFieldDisplayCollection.companyName?.isVisible
                                                            ? address.companyName
                                                            : ""
                                                    }
                                                    firstName={
                                                        addressFieldDisplayCollection.firstName?.isVisible
                                                            ? address.firstName
                                                            : ""
                                                    }
                                                    lastName={
                                                        addressFieldDisplayCollection.lastName?.isVisible
                                                            ? address.lastName
                                                            : ""
                                                    }
                                                    postalCode={
                                                        addressFieldDisplayCollection.postalCode?.isVisible
                                                            ? address.postalCode
                                                            : ""
                                                    }
                                                    phone={
                                                        addressFieldDisplayCollection.phone?.isVisible
                                                            ? address.phone
                                                            : ""
                                                    }
                                                    fax={
                                                        addressFieldDisplayCollection.fax?.isVisible ? address.fax : ""
                                                    }
                                                    email={
                                                        addressFieldDisplayCollection.email?.isVisible
                                                            ? address.email
                                                            : ""
                                                    }
                                                    state={
                                                        address.state && addressFieldDisplayCollection.state?.isVisible
                                                            ? address.state.abbreviation
                                                            : undefined
                                                    }
                                                    country={
                                                        address.country &&
                                                        addressFieldDisplayCollection.country?.isVisible
                                                            ? address.country.abbreviation
                                                            : undefined
                                                    }
                                                    extendedStyles={styles.addressDisplay}
                                                />
                                            )}
                                            {(!currentUserIsGuest ||
                                                (currentUserIsGuest &&
                                                    (allowCreateNewShipToAddress || !!newAddress || !!address))) && (
                                                <Link
                                                    {...styles.selectSavedAddressLink}
                                                    type="button"
                                                    onClick={selectAddressClickHandler}
                                                    data-test-selector={translate("Select Saved Address")}
                                                    aria-label={translate("Select Saved Address")}
                                                >
                                                    {translate("Select Saved Address")}
                                                </Link>
                                            )}
                                            <Modal
                                                {...styles.addressBookModal}
                                                headline={translate("Address Book")}
                                                isOpen={isCustomerSelectorModalOpen}
                                                handleClose={customerSelectorModalCloseHandler}
                                                data-test-selector="checkoutShipping_addressModal"
                                            >
                                                <ShipToSelector
                                                    currentShipTo={
                                                        useOneTimeAddress && shippingAddressFormState
                                                            ? undefined
                                                            : address
                                                    }
                                                    currentBillToId={currentBillToId}
                                                    newAddress={newAddress}
                                                    onSelect={selectCustomerHandler}
                                                    onEdit={editCustomerHandler}
                                                    onCreateNewAddressClick={newAddress && createNewAddressHandler}
                                                    extendedStyles={styles.shipToSelector}
                                                />
                                                <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                                                    <Button
                                                        {...styles.addressBookModalCancelButton}
                                                        onClick={customerSelectorModalCloseHandler}
                                                        data-test-selector="addressModal_cancel"
                                                        aria-label="Cancel"
                                                    >
                                                        {translate("Cancel")}
                                                    </Button>
                                                </StyledWrapper>
                                            </Modal>
                                        </StyledWrapper>
                                    )}
                            </Radio>
                        </>
                    )}
                    {oneTimeAddress && (
                        <Radio
                            {...styles.addressOptionRadio}
                            value="onetime"
                            data-test-selector="checkoutShipping_useOneTimeAddress"
                        >
                            {translate("Ship to One-Time Address")}
                            {!isOneTimeAddressModalOpen &&
                                shippingAddressFormState &&
                                shippingAddressFormState.address.oneTimeAddress && (
                                    <StyledWrapper {...styles.addressLinkWrapper}>
                                        <AddressInfoDisplay
                                            {...address}
                                            state={address.state ? address.state.abbreviation : undefined}
                                            country={address.country ? address.country.abbreviation : undefined}
                                            extendedStyles={styles.addressDisplay}
                                        />
                                        <Link
                                            {...styles.editAddressLink}
                                            type="button"
                                            onClick={() => setIsOneTimeAddressModalOpen(true)}
                                            data-test-selector="checkoutShipping_editAddress"
                                            aria-label={translate("Edit Address")}
                                        >
                                            {translate("Edit Address")}
                                        </Link>
                                    </StyledWrapper>
                                )}
                        </Radio>
                    )}
                </RadioGroup>
            </GridItem>
            <Modal
                {...styles.oneTimeAddressModal}
                headline={translate("Ship to One-Time Address")}
                isOpen={isOneTimeAddressModalOpen}
                handleClose={handleCloseOneTimeModalAddress}
                data-test-selector="checkoutShipping_oneTimeAddressModal"
            >
                <StyledForm {...styles.oneTimeAddressForm} onSubmit={submitOneTimeAddress} noValidate>
                    <ShippingAddressForm
                        countries={countries}
                        fieldDisplay={addressFieldDisplayCollection}
                        // Xng-Changes
                        extendedStyles={styles.oneTimeShippingAddress}
                    />
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...modalButtonsActionStyles.cancelButton}
                            onClick={handleCloseOneTimeModalAddress}
                            type="button"
                            data-test-selector="oneTimeAddressModal_cancel"
                            aria-label="Cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...modalButtonsActionStyles.submitButton}
                            type="submit"
                            data-test-selector="oneTimeAddressModal_submit"
                            aria-label="Use Address"
                        >
                            {translate("Use Address")}
                        </Button>
                    </StyledWrapper>
                </StyledForm>
            </Modal>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(ShippingAddress));
